import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { useSession } from '@/features/auth/api/useSession'
import { useClientsOverview } from '@/features/clients/api/useClientsOverview'
import { ClientMoneyTiles } from '@/features/clients/components/ClientMoneyTiles'
import { ClientsOverviewTable } from '@/features/clients/components/ClientsOverviewTable'
import { useYearParam, YearControl } from '@/features/clients/components/YearControl'
import { useCreateClient } from '@/features/directories/api/useDirectory'
import { ClientFormSheet, type ClientDraft } from '@/features/directories/components/DirectoryFormSheets'
import { clientSaveErrorMessage } from '@/features/directories/directoryText'
import { can, CAP, reaches, SURFACE } from '@/lib/can'
import type { ClientRegisteredState } from '@/routes/ClientPage'

/* The clients overview — the workbook's "Accounts Receivable Summary" as a
 * page. One row per client the caller may see, the year's figures beside the
 * whole position, and the totals as tiles above. Money is the server's
 * decision: `moneyUnavailable` says whether it is withheld (silently) or
 * failed (with a banner), and this page never consults the role itself.
 *
 * Creating a client lives here; every other record action lives on the
 * client's own page, which is where a reader is when they decide to act. */
export function ClientsPage() {
  const { t } = useTranslation()
  /* Behind its surface (#129, N-08): a role at «Нет доступа» on clients has no
     Clients stop, and a deep link here gets one refusal — not «Здесь пока
     пусто», which reads as a directory with nobody in it. The overview is its
     own component so no overview query fires for a refused reader. Reach is
     the session's (`reaches()`); the server scopes the rows regardless. */
  const { data: session, isPending: sessionPending } = useSession()

  if (sessionPending) {
    return (
      <>
        <PageHeader title={t('clients.title')} subtitle={t('clients.subtitle')} />
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }

  if (!reaches(session, SURFACE.clients)) {
    return (
      <>
        <PageHeader title={t('clients.title')} />
        <Card>
          <EmptyState
            title={t('clients.noAccess.title')}
            description={t('clients.noAccess.description')}
          />
        </Card>
      </>
    )
  }

  return <ClientsOverview canCreate={can(session, CAP.directoryClientCreate)} />
}

function ClientsOverview({ canCreate }: { canCreate: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [year, setYear] = useYearParam()
  const { data, isPending, isError, error, refetch } = useClientsOverview(year)
  const [creating, setCreating] = useState(false)
  const create = useCreateClient()

  /* The number is minted by the server inside the create, so the first moment
     it exists is the response. The client's page is where the reader lands,
     and it names the number in its receipt — handed over as router state, so
     only this navigation announces it. */
  const submit = (values: ClientDraft) => {
    create.mutate(
      { ...values },
      {
        onSuccess: (created) => {
          setCreating(false)
          const state: ClientRegisteredState = { registered: created.number }
          navigate(`/clients/${created.id}`, { state })
        },
      },
    )
  }

  const header = (
    <PageHeader
      title={t('clients.title')}
      subtitle={t('clients.subtitle')}
      actions={
        <>
          <YearControl years={data?.years ?? []} value={year} onChange={setYear} label={t('clients.year.label')} />
          {/* Absent without `directory.client.create` (#128, N-07), the one
              key the create route checks — a role without it would fill the
              sheet and be refused on «Сохранить». */}
          {canCreate ? (
            <Button size="sm" iconLeft={Plus} onClick={() => setCreating(true)}>
              {t('clients.new')}
            </Button>
          ) : null}
        </>
      }
    />
  )

  if (isPending) {
    return (
      <>
        {header}
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }
  if (isError) {
    return (
      <>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </>
    )
  }

  const showMoney = data.moneyUnavailable === undefined

  return (
    <>
      {header}

      {data.moneyUnavailable === 'unavailable' ? (
        <Banner tone="attention" title={t('clients.moneyUnavailable.title')}>
          {t('clients.moneyUnavailable.body')}
        </Banner>
      ) : null}

      {data.totals ? <ClientMoneyTiles money={data.totals} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('clients.table.label')}</CardTitle>
        </CardHeader>
        {create.error ? (
          <div className="px-3.5 pb-2">
            <ErrorState error={create.error} onRetry={() => create.reset()} />
          </div>
        ) : null}
        <ClientsOverviewTable
          rows={data.items}
          showMoney={showMoney}
          onOpen={(row) => navigate(`/clients/${row.id}`)}
        />
      </Card>

      {canCreate ? (
        <ClientFormSheet
          open={creating}
          onClose={() => setCreating(false)}
          pending={create.isPending}
          error={create.error ? clientSaveErrorMessage(create.error, t) : undefined}
          onSubmit={submit}
        />
      ) : null}
    </>
  )
}
